import React from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useCountry } from "../contexts/CountryContext";

const FAQPage = () => {
  const { country } = useCountry();
  const sections = [
    {
      title: 'For Customers',
      items: [
        { q: 'How do I request a quote?', a: 'Browse our services or categories, select the service you need, and fill in the quote request form with details about your job. Matched providers in your area will be notified and can send you personalised quotes.' },
        { q: 'Is HandyHub free for customers?', a: 'Yes. Requesting quotes and browsing providers is completely free. You only pay the provider directly for work you agree to. HandyHub does not charge customers any fees.' },
        { q: 'How do I compare providers?', a: 'When you receive multiple quotes, you can compare providers side by side on the quote detail page. See their ratings, years of experience, pricing, estimated timelines, and verification status all in one view.' },
        { q: 'Are providers verified?', a: 'Every provider on HandyHub is carefully reviewed before being approved. Providers who submit a government-issued ID and pass our review display the ID Verified badge on their profile. HandyHub reviews provider information during onboarding, however customers should independently assess providers before engaging services.' },
        { q: 'Can I leave a review?', a: 'Absolutely. Once your job is complete (or you\'ve received responses), you can leave a star rating and written review for each provider. Reviews are published and help other customers make informed decisions.' },
      ]
    },
    {
      title: 'For Providers',
      items: [
        { q: 'How do I join HandyHub?', a: 'Click "Join as a Provider" and create your account. You\'ll be guided through setting up your business profile, choosing your services, and selecting a subscription plan.' },
        { q: 'How do subscriptions work?', a: 'HandyHub offers monthly and yearly subscription plans. Your subscription gives you access to customer leads, a business profile, review management, and more. You can upgrade, downgrade, or cancel at any time.' },
        { q: 'How do I receive leads?', a: 'When a customer requests a quote for a service you offer in your area, you\'ll be matched and notified. You can review the job details and respond with your personalised quote.' },
        { q: 'How does verification work?', a: 'You can submit a government-issued photo ID through your provider dashboard. Our team reviews each submission and grants the ID Verified badge once approved. HandyHub no longer collects business registration or insurance documents — providers are responsible for holding any licences, registrations, or insurance required to legally provide their services.' },
        { q: 'How do I manage my services?', a: 'In your provider dashboard, go to the Services section. You can browse all available service categories and toggle which services you offer. This determines what customer requests you\'re matched with.' },
      ]
    },
    {
      title: 'General',
      items: [
        { q: 'What services are available on HandyHub?', a: 'HandyHub covers a wide range of home and business services including cleaning, plumbing, electrical, handyman, gardening, painting, pest control, renovations, IT services, and many more. Browse our categories page for the full list.' },
        { q: 'Is HandyHub available in my area?', a: 'HandyHub is expanding to new regions regularly. When you submit a quote request, we\'ll match you with providers in your area. If no providers are available yet, we\'ll notify you as soon as coverage expands.' },
        { q: 'How do I contact support?', a: `Visit our Contact page to send us a message, or email ${country.supportEmail}. We aim to respond within 24 hours on business days.` },
      ]
    },
  ];

  return (
    <div className="page" data-testid="faq-page" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div className="page-header" style={{ textAlign: 'center', marginBottom: '36px' }}>
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about using HandyHub.</p>
      </div>

      {sections.map(section => (
        <div key={section.title} style={{ marginBottom: '28px' }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px', paddingBottom: '8px', borderBottom: '2px solid var(--accent)' }}>{section.title}</h2>
          <Accordion type="single" collapsible>
            {section.items.map((item, idx) => (
              <AccordionItem key={idx} value={`${section.title}-${idx}`} style={{ borderColor: 'var(--border-color)' }}>
                <AccordionTrigger style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--text-dark)', textDecoration: 'none' }} data-testid={`faq-q-${idx}`}>
                  {item.q}
                </AccordionTrigger>
                <AccordionContent style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ))}

      <div style={{ textAlign: 'center', marginTop: '32px', padding: '28px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
        <p style={{ fontSize: '0.9375rem', color: 'var(--text-dark)', marginBottom: '12px' }}>Still have questions?</p>
        <Link to="/contact" className="btn-primary" data-testid="faq-contact-cta">Contact Support</Link>
      </div>
    </div>
  );
};

export default FAQPage;
